import React, { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import NewTicketModal from "../../components/NewTicketModal";
import {
  AddCircle,
  AccountTreeOutlined,
  EditOutlined,
  ContentCopy,
  DeleteOutline,
  PlayArrow,
  OpenInNew,
} from "@mui/icons-material";

import {
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Switch,
  IconButton,
  Stack,
} from "@mui/material";

import FlowBuilderModal from "../../components/FlowBuilderModal";

const reducer = (state, action) => {
  if (action.type === "LOAD_FLOWS") {
    return action.payload;
  }
  if (action.type === "UPDATE_FLOW") {
    const flow = action.payload;
    return state.map(f => f.id === flow.id ? flow : f);
  }
  if (action.type === "DELETE_FLOW") {
    return state.filter(f => f.id !== action.payload);
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles(theme => ({
  pageRoot: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    padding: theme.spacing(2),
    height: "calc(100% - 48px)",
    overflowY: "hidden",
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  pageHeader: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: "18px 22px",
    borderRadius: 16,
    background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
    border: "1px solid #bbf7d0",
    boxShadow: "0 2px 8px rgba(34,197,94,0.08)",
  },
  controlsPaper: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    padding: "12px 16px",
    backgroundColor: theme.palette.background.paper,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.default,
      fontSize: "0.85rem",
    },
    "& .MuiInputBase-input": {
      fontSize: "0.85rem",
      paddingTop: 11,
      paddingBottom: 11,
    },
  },
  addBtn: {
    minHeight: 42,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "8px 16px",
    textTransform: "none",
    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    boxShadow: "0 4px 12px rgba(34,197,94,0.30)",
    color: "#fff",
    "&:hover": { 
      background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
      boxShadow: "0 6px 16px rgba(34,197,94,0.40)",
    },
  },
  mainPaper: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    padding: "8px 12px",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  },
  tableHeader: {
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: "0.73rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  flowRow: {
    padding: "12px 12px",
    borderRadius: 10,
    marginTop: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: "all 0.15s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(15,23,42,0.025)",
      borderColor: "rgba(34,197,94,0.30)",
      boxShadow: "0 2px 8px rgba(34,197,94,0.08)",
    },
  },
  flowName: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
    letterSpacing: "-0.01em",
  },
  flowMeta: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  iconBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    "&:hover": {
      backgroundColor: "rgba(34,197,94,0.08)",
      borderColor: "rgba(34,197,94,0.40)",
    },
  },
}));

const FlowBuilder = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [flows, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket] = useState({});
  const [deletingFlow, setDeletingFlow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testingFlow, setTestingFlow] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testName, setTestName] = useState("Teste");
  const [testLoading, setTestLoading] = useState(false);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    setLoading(true);
    const fetchFlows = async () => {
      try {
        const { data } = await api.get("/flowbuilder");
        dispatch({ type: "LOAD_FLOWS", payload: data.flows });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchFlows();
  }, [reloadData]);

  useEffect(() => {
    const companyId = user.companyId;
    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_FLOW", payload: data.contact });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_FLOW", payload: +data.contactId });
      }
    };
    socket.on(`company-${companyId}-contact`, onContact);
    return () => { socket.off(`company-${companyId}-contact`, onContact); };
  }, [socket, user.companyId]);

  const filteredFlows = flows.filter(f =>
    !searchParam || f.name.toLowerCase().includes(searchParam)
  );

  const handleToggleActive = async (e, flow) => {
    e.stopPropagation();
    const newActive = !flow.active;
    // Optimistic update
    dispatch({ type: "UPDATE_FLOW", payload: { ...flow, active: newActive } });
    try {
      await api.put("/flowbuilder", { flowId: flow.id, active: newActive });
      toast.success(newActive ? "Fluxo ativado!" : "Fluxo desativado!");
    } catch (err) {
      // Revert on error
      dispatch({ type: "UPDATE_FLOW", payload: { ...flow, active: flow.active } });
      toastError(err);
    }
  };

  const handleDeleteFlow = async (flowId) => {
    try {
      await api.delete(`/flowbuilder/${flowId}`);
      dispatch({ type: "DELETE_FLOW", payload: flowId });
      toast.success("Fluxo excluído com sucesso");
      setDeletingFlow(null);
    } catch (err) { toastError(err); }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post("/flowbuilder/duplicate", { flowId });
      setReloadData(old => !old);
      toast.success("Fluxo duplicado com sucesso");
      setDeletingFlow(null);
    } catch (err) { toastError(err); }
  };

  const handleOpenEdit = (flow) => {
    setSelectedContactId(flow.id);
    setSelectedWebhookName(flow.name);
    setContactModalOpen(true);
  };

  const handleTestFlow = async () => {
    if (!testingFlow || !testPhone) return;
    setTestLoading(true);
    try {
      await api.post(`/flowbuilder/test/${testingFlow.id}`, {
        phone: testPhone,
        name: testName
      });
      toast.success(`Fluxo disparado para ${testPhone}!`);
      setTestModalOpen(false);
      setTestPhone("");
    } catch (err) { toastError(err); }
    finally { setTestLoading(false); }
  };

  return (
    <div className={classes.pageRoot}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          setNewTicketModalOpen(false);
          if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
        }}
      />
      <FlowBuilderModal
        open={contactModalOpen}
        onClose={() => { setSelectedContactId(null); setContactModalOpen(false); }}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData(old => !old)}
      />
      <ConfirmationModal
        title={deletingFlow ? `Excluir fluxo "${deletingFlow.name}"?` : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => deletingFlow && handleDeleteFlow(deletingFlow.id)}
      >
        Tem certeza? Todas as integrações relacionadas serão perdidas.
      </ConfirmationModal>
      <ConfirmationModal
        title={deletingFlow ? `Duplicar fluxo "${deletingFlow.name}"?` : ""}
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={() => deletingFlow && handleDuplicateFlow(deletingFlow.id)}
      >
        Uma cópia deste fluxo será criada.
      </ConfirmationModal>

      {/* Test Flow Modal */}
      {testModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: 28, width: 360,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", fontFamily: "Inter, sans-serif",
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", marginBottom: 6 }}>
              ▶ Testar Fluxo
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
              Fluxo: <strong>{testingFlow?.name}</strong>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                Telefone (com DDD)
              </label>
              <input
                type="text"
                placeholder="Ex: 11999998888"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value.replace(/\D/g, ""))}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
                  border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box",
                  fontFamily: "Inter, sans-serif",
                }}
                autoFocus
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                Nome do contato
              </label>
              <input
                type="text"
                placeholder="Ex: João Silva"
                value={testName}
                onChange={e => setTestName(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
                  border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box",
                  fontFamily: "Inter, sans-serif",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setTestModalOpen(false); setTestPhone(""); }}
                style={{
                  padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", color: "#64748b",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleTestFlow}
                disabled={!testPhone || testLoading}
                style={{
                  padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: "none", background: testPhone ? "#22c55e" : "#e2e8f0",
                  color: testPhone ? "#fff" : "#94a3b8", cursor: testPhone ? "pointer" : "not-allowed",
                }}
              >
                {testLoading ? "Enviando..." : "Disparar Fluxo"}
              </button>
            </div>
          </div>
        </div>
      )}

      <MainHeader>
        <Grid container style={{ width: "100%" }}>
          {/* Page header */}
          <Grid item xs={12}>
            <Paper elevation={0} className={classes.pageHeader}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 4px 10px rgba(34,197,94,0.28)", flexShrink: 0,
                    }}>
                      <AccountTreeOutlined style={{ color: "#fff", fontSize: 18 }} />
                    </div>
                    <div>
                      <Typography style={{ fontWeight: 700, fontSize: "1.05rem", color: "#0f172a", letterSpacing: "-0.02em" }}>
                        Fluxos de Conversa
                      </Typography>
                      <Typography style={{ fontSize: "0.78rem", color: "#4b7c52", marginTop: 2 }}>
                        Crie e gerencie automações inteligentes para o WhatsApp
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} md={4} style={{ textAlign: "right" }}>
                  <Chip
                    icon={<AccountTreeOutlined style={{ color: "#15803d", fontSize: 13 }} />}
                    label={`${flows.length} fluxo${flows.length !== 1 ? "s" : ""}`}
                    style={{
                      background: "rgba(34,197,94,0.12)", color: "#15803d",
                      border: "1px solid rgba(34,197,94,0.30)", fontWeight: 600, fontSize: "0.75rem",
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Search + Add */}
          <Grid item xs={12}>
            <Paper elevation={0} className={classes.controlsPaper}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    placeholder="Buscar fluxo..."
                    type="search"
                    value={searchParam}
                    onChange={e => setSearchParam(e.target.value.toLowerCase())}
                    variant="outlined"
                    className={classes.searchField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "gray", fontSize: 18 }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => { setSelectedContactId(null); setContactModalOpen(true); }}
                    className={classes.addBtn}
                  >
                    <AddCircle style={{ fontSize: 18, marginRight: 6 }} />
                    Novo Fluxo
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Table header */}
        <Grid container className={classes.tableHeader}>
          <Grid item xs={5}>Nome do Fluxo</Grid>
          <Grid item xs={3} style={{ textAlign: "center" }}>Ativo</Grid>
          <Grid item xs={4} style={{ textAlign: "right" }}>Ações</Grid>
        </Grid>

        {loading ? (
          <Stack justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress size={36} style={{ color: "#22c55e" }} />
          </Stack>
        ) : filteredFlows.length === 0 ? (
          <Stack justifyContent="center" alignItems="center" minHeight="30vh" gap={1}>
            <div style={{ fontSize: 40 }}>🔄</div>
            <Typography style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.95rem" }}>
              {searchParam ? "Nenhum fluxo encontrado" : "Nenhum fluxo criado"}
            </Typography>
            <Typography style={{ fontSize: "0.8rem", color: "#64748b" }}>
              {searchParam ? "Tente outro termo de busca" : "Clique em \"Novo Fluxo\" para começar"}
            </Typography>
          </Stack>
        ) : (
          filteredFlows.map(flow => (
            <Grid
              container
              key={flow.id}
              className={classes.flowRow}
              alignItems="center"
              onClick={() => history.push(`/flowbuilder/${flow.id}`)}
            >
              {/* Name */}
              <Grid item xs={5}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: flow.active
                      ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                      : "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: flow.active ? "0 2px 8px rgba(34,197,94,0.28)" : "none",
                    transition: "all 0.2s",
                  }}>
                    <AccountTreeOutlined style={{ color: "#fff", fontSize: 16 }} />
                  </div>
                  <div>
                    <div className={classes.flowName}>{flow.name}</div>
                    <div className={classes.flowMeta}>ID #{flow.id}</div>
                  </div>
                </div>
              </Grid>

              {/* Toggle */}
              <Grid item xs={3} style={{ textAlign: "center" }}>
                <div
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  onClick={e => e.stopPropagation()}
                >
                  <Switch
                    checked={!!flow.active}
                    onChange={e => handleToggleActive(e, flow)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#22c55e" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#22c55e" },
                    }}
                  />
                  <span style={{
                    fontSize: "0.75rem", fontWeight: 600,
                    color: flow.active ? "#15803d" : "#94a3b8",
                  }}>
                    {flow.active ? "Ativo" : "Inativo"}
                  </span>
                </div>
              </Grid>

              {/* Actions */}
              <Grid item xs={4}>
                <div
                  style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}
                  onClick={e => e.stopPropagation()}
                >
                  <Tooltip title="Editar nome">
                    <IconButton
                      size="small"
                      className={classes.iconBtn}
                      onClick={() => handleOpenEdit(flow)}
                    >
                      <EditOutlined style={{ fontSize: 15, color: "#64748b" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir fluxo">
                    <IconButton
                      size="small"
                      className={classes.iconBtn}
                      onClick={() => history.push(`/flowbuilder/${flow.id}`)}
                    >
                      <OpenInNew style={{ fontSize: 15, color: "#3b82f6" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicar">
                    <IconButton
                      size="small"
                      className={classes.iconBtn}
                      onClick={() => { setDeletingFlow(flow); setConfirmDuplicateOpen(true); }}
                    >
                      <ContentCopy style={{ fontSize: 15, color: "#64748b" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Testar fluxo">
                    <IconButton
                      size="small"
                      className={classes.iconBtn}
                      onClick={() => { setTestingFlow(flow); setTestModalOpen(true); }}
                      style={{ borderColor: "rgba(34,197,94,0.25)" }}
                    >
                      <PlayArrow style={{ fontSize: 15, color: "#22c55e" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      className={classes.iconBtn}
                      onClick={() => { setDeletingFlow(flow); setConfirmOpen(true); }}
                      style={{ borderColor: "rgba(239,68,68,0.25)" }}
                    >
                      <DeleteOutline style={{ fontSize: 15, color: "#ef4444" }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </Grid>
            </Grid>
          ))
        )}
      </Paper>
    </div>
  );
};

export default FlowBuilder;
